import React, { useEffect, useRef, useState } from "react";
import { cn } from "../../utils/cn";
import { suggestClients } from "../../lib/clients";

/**
 * Input de telefon cu autocomplete intern.
 * Apelează suggestClients cu cifrele tastate (min 3) și filtrează local
 * doar clienții al căror număr de telefon conține cifrele introduse.
 */
const PhoneAutocomplete = React.forwardRef(({
  value = "",
  onChange,
  onPickClient,
  label,
  labelClassName,
  placeholder = "07xx xxx xxx",
  error,
  required = false,
  disabled = false,
  id,
}, ref) => {
  const inputId = id || `phone-${Math.random().toString(36).substring(2, 9)}`;
  const [suggestions, setSuggestions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const debounceRef = useRef(null);
  const wrapRef = useRef(null);

  useEffect(() => {
    const digits = String(value || "").replace(/\D/g, "");
    if (digits.length < 3) {
      setSuggestions([]);
      setIsOpen(false);
      return;
    }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const rows = await suggestClients(digits, { limit: 8 });
        // păstrăm doar clienții al căror telefon conține cifrele tastate
        const filtered = (Array.isArray(rows) ? rows : []).filter((c) =>
          String(c.phone || "").replace(/\D/g, "").includes(digits)
        );
        setSuggestions(filtered);
        if (filtered.length > 0) setIsOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 250);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value]);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (!wrapRef.current?.contains(e.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handler);
    document.addEventListener("touchstart", handler);
    return () => {
      document.removeEventListener("mousedown", handler);
      document.removeEventListener("touchstart", handler);
    };
  }, [isOpen]);

  return (
    <div className="space-y-2 relative" ref={wrapRef}>
      {label && (
        <label
          htmlFor={inputId}
          className={cn(
            "text-sm font-medium leading-none",
            error ? "text-destructive" : "text-foreground",
            labelClassName
          )}
        >
          {label}
          {required && <span className="text-destructive ml-1">*</span>}
        </label>
      )}

      <input
        ref={ref}
        id={inputId}
        type="tel"
        className={cn(
          "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-destructive focus-visible:ring-destructive"
        )}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        onFocus={() => suggestions.length > 0 && setIsOpen(true)}
        required={required}
        disabled={disabled}
      />

      {isOpen && suggestions.length > 0 && (
        <div className="absolute z-[300] top-full mt-1 w-full rounded-md border border-border bg-surface shadow-lg max-h-60 overflow-auto">
          {suggestions.map((c) => (
            <button
              key={c.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onTouchStart={(e) => e.preventDefault()}
              onClick={() => {
                onPickClient?.(c);
                setSuggestions([]);
                setIsOpen(false);
              }}
              className="w-full text-left px-3 py-2 hover:bg-muted transition-colors"
            >
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-medium">{c.name}</span>
                {c.is_vip && <span className="text-xs">⭐</span>}
              </div>
              <div className="text-xs text-muted-foreground">
                {c.phone || ""}
                {c.email ? ` • ${c.email}` : ""}
              </div>
            </button>
          ))}
        </div>
      )}

      {error && <p className="text-sm text-destructive">{error}</p>}
    </div>
  );
});

PhoneAutocomplete.displayName = "PhoneAutocomplete";
export default PhoneAutocomplete;
